import sortBy from 'lodash/sortBy';
import { useCallback, useMemo, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { ApolloError, useApolloClient } from '@apollo/client';

import { PRIVATE_CLASS } from 'global/private-routes.constants';
import { isAssignmentNotFoundError } from 'approot/errors/is-assignment-not-found-error';
import {
  ACTIVITIES_COMPONENT_TYPE_COLUMNS,
  ACTIVITIES_COMPONENT_TYPE_ACTIVITY,
  LESSON_ACTIVITIES,
  STUDENT_ASSIGNMENT_STATUS_NOT_STARTED,
} from 'global/constants';
import { ComponentColumns } from 'approot/activity-builder/components/component.constants';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import ActivityBuilderZoomComponentModal from '../../activity-builder-zoom-component-modal';
import type { InteractiveLessonSectionComponentFragment } from '../../lesson-builder.graphql.generated';
import StudentColumns from './columns/student-columns';
import { SectionOrSubSection } from '../../activity-builder.constants';
import {
  ACTIVITY_BUILDER_EVENT_COMPONENT_EXPAND,
  ComponentEventData,
  ComponentExpandModeType,
  pushComponentViewEvent,
} from '../../present/activity-builder-present-data-tracking.utils';
import ComponentsViewWrapper from 'approot/activity-builder/components/components-view-wrapper';
import StudentComponents from './student-components';
import { BatchStudentAnswersContextProvider } from 'approot/activity-builder/components/shared/batch-student-answer.context';
import { getStyleProperties } from 'approot/activity-builder/components/components.utils';
import {
  getAppliedColour,
  isBackgroundApplied,
  isBorderApplied,
} from 'approot/activity-builder/components/shared/component-settings-borders.utils';
import { isStudentClosedAssignmentError } from 'approot/activity-builder/student/present/answers/student-closed-assignment.utils';
import StudentClosedAssignmentModal from 'approot/activity-builder/student/present/answers/student-closed-assignment-modal';
import { updateIsAssignedInCache } from 'approot/activity-builder/activity-builder.apollo.utils';
import { useGetInteractiveLessonParams } from 'approot/activity-builder/activity-builder.hooks';
import { useGetStudentLessonAssignmentById } from '../../../student-lesson/hooks/use-get-student-lesson-assignment-by-id';
import { useGetStudentAssessmentAssignmentById } from '../../../student-assessment/hooks/use-get-student-assessment-assignment-by-id';
import { getStudentClassInfo } from 'approot/shared/api/auth/student-user-auth';

type Props = {
  section: SectionOrSubSection;
  contentId: number;
  isLoggedIn: boolean;
  eventTrackingData?: ComponentEventData;
};

const StudentComponentsView = ({
  section,
  contentId,
  isLoggedIn,
  eventTrackingData,
}: Props) => {
  const client = useApolloClient();
  const navigate = useNavigate();

  const [zoomedComponent, setZoomedComponent] = useState<
    InteractiveLessonSectionComponentFragment | undefined
  >();

  const components = section?.components;
  const sortedComponents = useMemo(
    () => sortBy(components, 'sort'),
    [components]
  );

  const { contentType, lang, version, assignmentId, classId } =
    useGetInteractiveLessonParams();

  const isLesson = contentType === LESSON_ACTIVITIES;

  const { assignment: lessonAssignment } = useGetStudentLessonAssignmentById({
    fetchPolicy: 'cache-only',
    classId,
    assignmentId,
    language: lang,
    skip: !isLesson,
  });

  const { assignment: assessmentAssignment } =
    useGetStudentAssessmentAssignmentById({
      fetchPolicy: 'cache-only',
      classId,
      assignmentId,
      language: lang,
      skip: isLesson,
    });

  const { isVisible, openModal, closeModal } = useSimpleModalState();
  const {
    isVisible: isClosedAssignmentModalVisible,
    openModal: openClosedAssignmentModal,
    closeModal: closeClosedAssignmentModal,
  } = useSimpleModalState();

  const handleOpenModal = useCallback(
    (
      component: InteractiveLessonSectionComponentFragment,
      expandMode?: ComponentExpandModeType
    ) => {
      setZoomedComponent(component);
      openModal();
      pushComponentViewEvent({
        component,
        eventType: ACTIVITY_BUILDER_EVENT_COMPONENT_EXPAND,
        expandMode,
        eventTrackingData,
        contentId,
      });
    },
    [contentId, eventTrackingData, openModal]
  );

  const onUpsertError = useCallback(
    (error: ApolloError) => {
      // When an Assignment is not found when saving an answer send student
      // back to this class's assignments view
      if (isAssignmentNotFoundError(error)) {
        const studentClassInfo = getStudentClassInfo();
        if (studentClassInfo?.classToDoId) {
          navigate(
            generatePath(PRIVATE_CLASS, {
              classId: studentClassInfo.classToDoId,
            })
          );
        }
        return;
      }

      if (isStudentClosedAssignmentError(error)) {
        openClosedAssignmentModal();
        updateIsAssignedInCache({
          client,
          id: contentId,
          contentType,
          isAssigned: false,
          language: lang,
          version: version || '',
        });
      }
    },
    [
      navigate,
      openClosedAssignmentModal,
      client,
      contentId,
      contentType,
      lang,
      version,
    ]
  );

  const onUpsertSuccess = useCallback(() => {
    if (
      (isLesson && lessonAssignment?.isAssigned) ||
      (!isLesson && assessmentAssignment?.isAssigned)
    ) {
      updateIsAssignedInCache({
        client,
        id: contentId,
        contentType,
        isAssigned: true,
        language: lang,
        version: version || '',
      });
    }
  }, [
    client,
    contentId,
    contentType,
    lang,
    version,
    isLesson,
    lessonAssignment,
    assessmentAssignment,
  ]);

  if (!sortedComponents) return null;

  const assignment = isLesson ? lessonAssignment : assessmentAssignment;
  // Not gated on isAutomarkable: progress is tracked for every digital assessment,
  // so a later automarkable flip finds the rows already there (TT-1649.). Lessons
  // have no studentProgress, so they never reach not-started and never fire.
  const skipAssignmentStartedCall =
    assignment?.studentProgress !== STUDENT_ASSIGNMENT_STATUS_NOT_STARTED;

  return (
    <BatchStudentAnswersContextProvider
      onUpsertError={onUpsertError}
      onUpsertSuccess={onUpsertSuccess}
      skipAssignmentStartedCall={skipAssignmentStartedCall}
    >
      {sortedComponents.map(component => {
        return component.type !== ACTIVITIES_COMPONENT_TYPE_ACTIVITY ? (
          <ComponentsViewWrapper
            key={component.id}
            id={component.id}
            type={component.type}
            colours={getAppliedColour(getStyleProperties(component))}
            showBorder={isBorderApplied(getStyleProperties(component))}
            showBackground={isBackgroundApplied(getStyleProperties(component))}
          >
            {component.type === ACTIVITIES_COMPONENT_TYPE_COLUMNS ? (
              <StudentColumns
                {...(component as ComponentColumns)}
                contentId={contentId}
                eventTrackingData={eventTrackingData}
              />
            ) : (
              <StudentComponents
                component={component}
                contentId={contentId}
                eventTrackingData={eventTrackingData}
                isLoggedIn={isLoggedIn}
                handleOpenModal={handleOpenModal}
              />
            )}
          </ComponentsViewWrapper>
        ) : null;
      })}

      {isVisible && zoomedComponent && (
        <ActivityBuilderZoomComponentModal
          isStudent
          component={zoomedComponent}
          closeModal={() => {
            setZoomedComponent(undefined);
            closeModal();
          }}
        />
      )}

      {isClosedAssignmentModalVisible && (
        <StudentClosedAssignmentModal onClose={closeClosedAssignmentModal} />
      )}
    </BatchStudentAnswersContextProvider>
  );
};

export default StudentComponentsView;
